import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { convertToNepaleseRupees, roundUsdPrice } from '../utils/tourAlgorithms';

const API_URL = import.meta.env.VITE_API_URL || '/api';
const FALLBACK_RATE = 154.46;
const REFRESH_INTERVAL_MS = 60 * 60 * 1000;

const ExchangeRateContext = createContext({
  rate: FALLBACK_RATE,
  buy: null,
  sell: FALLBACK_RATE,
  effectiveDate: null,
  source: 'Fallback',
  isFallback: true,
  loading: true,
  refreshRate: async () => {},
  roundUsd: roundUsdPrice,
  usdToNpr: (amount) => convertToNepaleseRupees(amount, FALLBACK_RATE),
});

const isValidRate = (value) => {
  const rate = Number(value);
  return Number.isFinite(rate) && rate >= 100 && rate <= 250;
};

export function ExchangeRateProvider({ children }) {
  const [snapshot, setSnapshot] = useState({
    rate: FALLBACK_RATE,
    buy: null,
    sell: FALLBACK_RATE,
    effectiveDate: null,
    source: 'Fallback',
    isFallback: true,
  });
  const [loading, setLoading] = useState(true);
  const lastFetchedAt = useRef(0);

  const refreshRate = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/exchange-rate`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(`Exchange-rate request failed (${response.status})`);

      const data = await response.json();
      if (!isValidRate(data.rate)) throw new Error('Exchange-rate response was invalid');

      setSnapshot({
        rate: Number(data.rate),
        buy: isValidRate(data.buy) ? Number(data.buy) : null,
        sell: isValidRate(data.sell) ? Number(data.sell) : Number(data.rate),
        effectiveDate: data.effectiveDate || null,
        source: data.source || 'Nepal Rastra Bank',
        isFallback: Boolean(data.isFallback),
      });
      lastFetchedAt.current = Date.now();
    } catch (_error) {
      // Keep the most recent valid snapshot. The initial state is a safe fallback.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshRate();
    const interval = window.setInterval(refreshRate, REFRESH_INTERVAL_MS);
    const handleOnline = () => refreshRate();
    const handleVisibility = () => {
      if (
        document.visibilityState === 'visible'
        && Date.now() - lastFetchedAt.current >= REFRESH_INTERVAL_MS
      ) {
        refreshRate();
      }
    };

    window.addEventListener('online', handleOnline);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('online', handleOnline);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [refreshRate]);

  const value = useMemo(() => ({
    ...snapshot,
    loading,
    refreshRate,
    roundUsd: roundUsdPrice,
    usdToNpr: (amount) => convertToNepaleseRupees(amount, snapshot.rate),
  }), [loading, refreshRate, snapshot]);

  return (
    <ExchangeRateContext.Provider value={value}>
      {children}
    </ExchangeRateContext.Provider>
  );
}

ExchangeRateProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useExchangeRate = () => useContext(ExchangeRateContext);
